/**
 * Deterministic template blurbs (§7.7, FR-32 – FR-36, FR-77, FR-79).
 *
 * On a hosted site the `none` provider is the default, and per FR-79 it is now
 * the *only* provider — there is no LLM call anywhere in this module. That
 * constraint shaped it from the start: it does not fill in a fixed sentence
 * with a name. It reads a structured evidence packet, decides which two or
 * three facts about this player are actually the interesting ones right now,
 * and writes those.
 *
 * This answers "why him, for *your* team, right now" — team fit, timing,
 * scarcity — computed live from local data (FR-33), with no invented
 * statistics anywhere in this file. The separate, complementary question "why
 * is he good in general" (role, scheme, situation) needs real-world research
 * this module has no access to; that lives in `core/data/scoutingNotes.ts` as
 * dated, statically authored content instead (FR-79, D-7).
 *
 * Pure module — no I/O.
 */

import type {
  AdpRow,
  DraftState,
  LeagueConfig,
  Player,
  Valuation,
} from './types.ts';
import type { PlayerRankView } from './blend.ts';
import type { RosterNeed, ScarcityState } from './valuation.ts';
import type { WaitAdvice } from './availability.ts';

/** The complete evidence packet the template writer reasons from (FR-33). */
export interface BlurbEvidence {
  player: Player;
  valuation: Valuation;
  /** How the *active* board ranks him — what "on your board" means to the user. */
  rankView: PlayerRankView | undefined;
  /**
   * The same player across every loaded source, equally weighted (FR-13).
   *
   * Kept separate from `rankView` because the two answer different questions:
   * `rankView` is the board the user is actually looking at, while this is the
   * cross-source comparison the disagreement sentence reasons about. When only
   * one source is loaded they are the same object.
   */
  comparison: PlayerRankView | undefined;
  adp: AdpRow | undefined;
  wait: WaitAdvice | undefined;
  scarcity: ScarcityState;
  need: RosterNeed;
  league: LeagueConfig;
  draft: DraftState;
  tier: number | undefined;
  /** Free-text notes carried through from the ranking source, if any. */
  notes: string | null;
}

export interface Blurb {
  /** 3–5 sentences making the case (FR-32). */
  case: string[];
  /** 2–3 risks. */
  risks: string[];
  provider: 'none';
  generated_at: string;
}

export interface BlurbOptions {
  /**
   * Drop the timing sentence from the case.
   *
   * The case is written to stand alone, so it normally includes the wait-vs-take
   * read. A caller that already shows `wait.sentence` in its own section — the
   * player drawer does — would otherwise print the identical sentence twice on
   * one screen, which reads like a bug rather than emphasis.
   */
  omitTiming?: boolean;
}

export function generateTemplateBlurb(ev: BlurbEvidence, opts: BlurbOptions = {}): Blurb {
  return {
    case: buildCase(ev, opts),
    risks: buildRisks(ev),
    provider: 'none',
    generated_at: new Date().toISOString(),
  };
}

function buildCase(ev: BlurbEvidence, opts: BlurbOptions): string[] {
  const { player, valuation: v } = ev;
  const position = player.position;
  const pos = position ?? 'FLEX';
  const name = player.full_name;
  const out: string[] = [];

  // 1. Open with the strongest true thing about this player right now.
  const posRank = ev.rankView?.ranks.find((r) => r.position_rank !== null)?.position_rank;
  const openers: { weight: number; text: string }[] = [];

  if (v.vor > 0) {
    openers.push({
      weight: v.contributions.vor_z,
      text: `${name} projects ${v.proj_points.toFixed(0)} points in your scoring, ${v.vor.toFixed(0)} clear of a replacement-level ${pos} — the largest edge available at the position right now.`,
    });
  }
  if (ev.rankView && ev.rankView.blended_percentile > 0.7) {
    openers.push({
      weight: v.contributions.rank_score,
      text: `${name} is the ${ordinal(ev.rankView.blended_rank)} player on your board${posRank ? ` and ${pos}${posRank}` : ''}, and he is still there at pick ${ev.draft.current_pick_no}.`,
    });
  }
  if (position && ev.need.unfilled.includes(position)) {
    openers.push({
      weight: v.contributions.need,
      text: `You have no starting ${pos} yet, and ${name} is the best one left.`,
    });
  }
  openers.sort((a, b) => b.weight - a.weight);
  out.push(openers[0]?.text ?? `${name} is the top-scoring option on the board by the engine's composite.`);

  // 2. The value-versus-market sentence, when there is a real gap to report.
  if (ev.adp) {
    const delta = ev.adp.adp - ev.draft.current_pick_no;
    if (delta >= 8) {
      out.push(
        `He is going ${Math.round(delta)} picks later than his ADP of ${ev.adp.adp.toFixed(1)} — this is a genuine fall, not a reach.`,
      );
    } else if (delta <= -8) {
      out.push(
        `Taking him here is ${Math.abs(Math.round(delta))} picks ahead of his ${ev.adp.adp.toFixed(1)} ADP, so you are paying up rather than finding value.`,
      );
    }
  }

  // 3. Disagreement between sources is the most interesting thing a
  //    multi-source tool knows, so say it out loud when it is large.
  const cmp = ev.comparison ?? ev.rankView;
  if (cmp && cmp.disagreement >= 12 && cmp.ranks.length >= 2) {
    const sorted = [...cmp.ranks].filter((r) => !r.imputed).sort((a, b) => a.overall_rank - b.overall_rank);
    const high = sorted[0];
    const low = sorted[sorted.length - 1];
    if (high && low && high.source_id !== low.source_id) {
      out.push(
        `Your sources disagree about him: ${high.source_name} has him ${high.overall_rank}, ${low.source_name} has him ${low.overall_rank}.`,
      );
    }
  }

  // 4. Timing.
  if (!opts.omitTiming && ev.wait && ev.wait.verdict !== 'unknown') {
    out.push(ev.wait.sentence);
  }

  // 5. Positional context, but only when it is doing work.
  const tierRemaining = position ? ev.scarcity.tier_remaining[position] ?? 0 : 0;
  if (position && ev.scarcity.runs.includes(position)) {
    const rate = ev.scarcity.recent_rate[position] ?? 0;
    out.push(
      `${pos} is in a run — ${Math.round(rate * ev.scarcity.window)} of the last ${ev.scarcity.window} picks, with ${tierRemaining} left in the current tier.`,
    );
  } else if (position && ev.tier !== undefined && tierRemaining <= 2 && tierRemaining > 0) {
    out.push(
      tierRemaining === 1
        ? `He is the last player in ${pos} tier ${ev.tier}; the next tier is a real step down.`
        : `He is one of the last ${tierRemaining} players in ${pos} tier ${ev.tier}; the next tier is a real step down.`,
    );
  }

  if (ev.notes) out.push(`Source note: ${ev.notes}`);

  // FR-33: say "insufficient data" rather than invent. FR-32 asks for 3-5
  // sentences, but padding to that length with filler would be worse than
  // being short — so when the engine genuinely had little to work with, name
  // what was missing instead. That is a useful sentence: it tells the user
  // which source to load to get a better answer.
  if (out.length < 3) {
    const missing: string[] = [];
    if (!ev.adp) missing.push('no ADP for him');
    if ((cmp?.ranks.filter((r) => !r.imputed).length ?? 0) < 2) {
      missing.push('only one ranking source loaded');
    }
    if (missing.length > 0) {
      out.push(
        `Insufficient data for a fuller case on ${name} — ${missing.join(' and ')}. Adding a second source would sharpen this.`,
      );
    }
  }

  return out.slice(0, 5);
}

function buildRisks(ev: BlurbEvidence): string[] {
  const { player, valuation: v } = ev;
  const pos = player.position ?? 'FLEX';
  const out: string[] = [];

  if (player.injury_status) {
    out.push(`Listed ${player.injury_status}${player.status && player.status !== 'Active' ? ` (${player.status})` : ''}.`);
  }

  if (v.bye_penalty > 0) {
    out.push(
      `Week ${player.bye_week} bye stacks with your existing ${pos}s — you would be short a starter that week.`,
    );
  }

  if (ev.adp && ev.adp.stdev > ev.adp.adp * 0.35 && ev.adp.times_drafted >= 10) {
    out.push(
      `The market is split on him: ADP ${ev.adp.adp.toFixed(1)} with a ${ev.adp.stdev.toFixed(1)}-pick spread across ${ev.adp.times_drafted.toLocaleString()} drafts.`,
    );
  }

  if (ev.adp && ev.wait && (ev.wait.confidence === 'low' || ev.wait.confidence === 'none')) {
    out.push(`Thin ADP sample, so the "will he last" read on him is weak.`);
  }

  if (v.need_score < 0.4) {
    out.push(
      `This would be roster depth rather than a starter — your ${pos} slots are already covered.`,
    );
  }

  if (v.estimated_projection) {
    out.push(
      `Projection is curve-estimated from his rank, not a real stat projection, so it cannot disagree with your ranking source about him.`,
    );
  }

  if (player.years_exp === 0) {
    out.push(`Rookie — no NFL usage to price him on, and rank-derived projections are least reliable here.`);
  }

  return out.slice(0, 3);
}

function ordinal(n: number): string {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] ?? s[v] ?? s[0]!);
}
